import asyncParallel from 'async/parallel';
import asyncSeries from 'async/series';
import asyncTimesLimit from 'async/timesLimit';
import { ethers } from 'ethers';

import EthTools from './eth_tools';
import SWAP_ABI from './harbour_swap.json';

const SWAP_INTERFACE = new ethers.utils.Interface(SWAP_ABI);
const LIST_LIMIT = 5;
const SETTLE_MAP = {
  0: 'both',
  1: 'sell',
  2: 'buy',
};
const ZERO = '0x0000000000000000000000000000000000000000';

export default {
  roundToSettle,
  getWindowBlocks,
  getMarketData,
  getBuyOrder,
  getSellOrder,
  postBuy,
  postSell,
  cancelBuy,
  cancelSell,
  settleBucketExact,
  settleBucketFlatSell,
  settleBucketFlatBuy,
  fetchMarket,
};
function roundToSettle(block_number: number, window_blocks: number): number {
  return block_number - (block_number % window_blocks);
}
function getWindowBlocks(params, done) {
  const contract = _getContract(params);
  contract.windowBlocks().then((blocks) => done(null, blocks), done);
}
function getMarketData(params, done) {
  const contract = _getContract(params);
  contract
    .getMarketData(params.token_addr, params.currency_addr, params.price_ratio)
    .then((result) => {
      const data = {
        buy_order_count: result.buyOrderCount.toNumber(),
        sell_order_count: result.sellOrderCount.toNumber(),
        last_settle_direction: SETTLE_MAP[result.lastSettleDirection],
        last_settle_block: result.lastSettledBlock,
      };
      done(null, data);
    }, done);
}
function getBuyOrder(params, done) {
  const contract = _getContract(params);
  contract
    .getBuyOrder(
      params.token_addr,
      params.currency_addr,
      params.price_ratio,
      params.index
    )
    .then(
      (result) => done(null, _mapOrder(result)),
      (err) => done(err)
    );
}
function getSellOrder(params, done) {
  const contract = _getContract(params);
  contract
    .getSellOrder(
      params.token_addr,
      params.currency_addr,
      params.price_ratio,
      params.index
    )
    .then(
      (result) => done(null, _mapOrder(result)),
      (err) => done(err)
    );
}
function _mapOrder(result) {
  return {
    buyer: result.buyer,
    seller: result.seller,
    quantity: result.quantity,
    start_block: result.startBlock,
    end_block: result.endBlock,
  };
}
function postBuy(params, done) {
  const contract = _getContract(params);
  contract.populateTransaction
    .postBuy(
      params.token_addr,
      params.currency_addr,
      params.price_bucket,
      params.price_exp,
      params.start_block,
      params.end_block,
      params.quantity
    )
    .then(
      (tx) => {
        tx.chainId = params.chain_id;
        if (params.currency_addr === ZERO) {
          tx.value = params.spend_wei.toHexString();
        }
        done(null, tx);
      },
      (err: any) => done(err)
    );
}
function postSell(params, done) {
  const contract = _getContract(params);
  contract.populateTransaction
    .postSell(
      params.token_addr,
      params.currency_addr,
      params.price_bucket,
      params.price_exp,
      params.start_block,
      params.end_block,
      params.quantity
    )
    .then(
      (tx) => {
        tx.chainId = params.chain_id;
        done(null, tx);
      },
      (err: any) => done(err)
    );
}
function cancelBuy(params, done) {
  const contract = _getContract(params);
  contract.populateTransaction
    .cancelBuy(
      params.token_addr,
      params.currency_addr,
      params.price_ratio,
      params.index,
      false
    )
    .then(
      (tx) => {
        tx.chainId = params.chain_id;
        done(null, tx);
      },
      (err: any) => done(err)
    );
}
function cancelSell(params, done) {
  const contract = _getContract(params);
  contract.populateTransaction
    .cancelSell(
      params.token_addr,
      params.currency_addr,
      params.price_ratio,
      params.index,
      false
    )
    .then(
      (tx) => {
        tx.chainId = params.chain_id;
        done(null, tx);
      },
      (err: any) => done(err)
    );
}
function settleBucketExact(params, done) {
  _settle(params, 'settleBucketExact', done);
}
function settleBucketFlatSell(params, done) {
  _settle(params, 'settleBucketFlatSell', done);
}
function settleBucketFlatBuy(params, done) {
  _settle(params, 'settleBucketFlatBuy', done);
}
function _settle(params, name, done) {
  const contract = _getContract(params);
  contract.populateTransaction[name](
    params.token_addr,
    params.currency_addr,
    params.price_ratio,
    params.settle_block
  ).then(
    (tx) => {
      tx.chainId = params.chain_id;
      done(null, tx);
    },
    (err: any) => done(err)
  );
}
function fetchMarket(params, done) {
  let market;
  asyncSeries(
    [
      (done) =>
        _fetchMarketInfo(params, (err, data) => {
          market = data;
          done(err);
        }),
      (done) => {
        const { sell_order_count, buy_order_count } = market;
        if (sell_order_count > 0 && buy_order_count > 0) {
          asyncParallel(
            [
              (done) => {
                const opts = {
                  ...params,
                  count: buy_order_count,
                  func: getBuyOrder,
                };
                _fetchOrderList(opts, (err, list) => {
                  market.buy_list = list;
                  done(err);
                });
              },
              (done) => {
                const opts = {
                  ...params,
                  count: sell_order_count,
                  func: getSellOrder,
                };
                _fetchOrderList(opts, (err, list) => {
                  market.sell_list = list;
                  done(err);
                });
              },
            ],
            done
          );
        } else {
          done();
        }
      },
    ],
    (err) => {
      if (!err) {
        market.settle_direction = _calcSettle(market);
      }
      done(err, market);
    }
  );
}
function _fetchOrderList(params, done) {
  const { func, count, ...other } = params;
  const list = [];
  asyncTimesLimit(
    count,
    LIST_LIMIT,
    (index, done) => {
      func({ ...other, index }, (err, data) => {
        list[index] = data;
        done(err);
      });
    },
    (err) => {
      done(err, list);
    }
  );
}
function _fetchMarketInfo(params, done) {
  const { chain_id } = params;
  let market_data;
  let window_blocks;
  let block_number;
  asyncParallel(
    [
      (done) =>
        getMarketData(params, (err, data) => {
          market_data = data;
          done(err);
        }),
      (done) =>
        getWindowBlocks(params, (err, blocks) => {
          window_blocks = blocks;
          done(err);
        }),
      (done) =>
        EthTools.getProvider(chain_id)
          .getBlockNumber()
          .then(
            (num) => {
              block_number = num;
              done();
            },
            (err) => {
              done(err);
            }
          ),
    ],
    (err) => {
      let result;
      if (!err) {
        result = market_data;
        result.window_blocks = window_blocks;
        result.block_number = block_number;
        result.settle_block = roundToSettle(block_number, window_blocks);
      }
      done(err, result);
    }
  );
}
function _calcSettle(market: any): string {
  let ret = '';
  if (market && market.buy_list && market.sell_list) {
    const { settle_block, buy_list, sell_list } = market;
    const sell_quantity = _sumQuantity(sell_list, settle_block);
    const buy_quantity = _sumQuantity(buy_list, settle_block);

    if (sell_quantity.gt(0) && buy_quantity.gt(0)) {
      if (sell_quantity.eq(buy_quantity)) {
        ret = 'exact';
      } else if (sell_quantity.gt(buy_quantity)) {
        ret = 'sell';
      } else {
        ret = 'buy';
      }
    }
  }
  return ret;
}
function _sumQuantity(list, settle_block) {
  let ret = ethers.BigNumber.from(0);
  list.forEach((order) => {
    const { start_block, end_block } = order;
    if (settle_block >= start_block && settle_block <= end_block) {
      ret = ret.add(order.quantity);
    }
  });
  return ret;
}

function _getContract(params) {
  const { chain_id, contract_addr } = params;
  const provider = EthTools.getProvider(chain_id);
  const contract = new ethers.Contract(contract_addr, SWAP_INTERFACE, provider);
  return contract;
}
